import Head from 'next/head';
import PropTypes from 'prop-types';
import { NextSeo } from 'next-seo';
import PracticePageLanding from '@cont/Practice/Listing/Landing';
import { fetchTopologyInfo } from '@actions/topologies';
import {
  fetchPracticeTopologies,
  fetchLearnerDetails,
  fetchPracticeAvailableLanguages
} from '@actions/practiceSession';
import { useSetSelectedNav } from '@stores/sidenav';
import pushRoute from '@utils/pushRoute';
import { PRACTICE_PAGE } from '@constants/segment';
import setupInitialProps from '@utils/setupInitialProps';
import { getPracticeAvailableLanguages } from '@selectors/practiceSession';
import { redirectToRoute } from '@utils/route';
import GTPMobileBlocker from '@cont/GTP/GTPMobileBlocker';
import useWindowSize from '@hooks/useWindowSize';
import Layout from '@layout/index';
import fetchIsGoalGTP from '@api/fetchers/constants';
import { fetchPracticeByEducators } from '@api/fetchers/practice';
import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';
import OneLinkSmartScript from '@Analytics/OneLinkSmartScript';

const PracticePage = ({ goalInfo, isGTP, practicesByEducators = {} }) => {
  const { title } = goalInfo || {};
  const metaTitle = `My Practice | ${title} | Unacademy`;
  const { isMobile } = useWindowSize();

  useSetSelectedNav('practice');

  if (isGTP && isMobile) {
    return <GTPMobileBlocker goalInfo={goalInfo || {}} />;
  }
  return (
    <>
      <NextSeo
        title={metaTitle}
        openGraph={{
          title: metaTitle
        }}
      />
      <Head>
        <title>{metaTitle}</title>
      </Head>
      <PracticePageLanding practicesByEducators={practicesByEducators} />
      <OneLinkSmartScript />
    </>
  );
};

PracticePage.pageInfo = {
  isMWebSupported: true,
  name: PRACTICE_PAGE,
  layoutOptions: {
    hideFreshbot: true,
    hideFooter: true,
    showPlusNav: true
  }
};

export default PracticePage;

PracticePage.getInitialProps = setupInitialProps(
  async ({ store, query, isServer, res }) => {
    const state = store.getState();
    const { accessToken, isLoggedIn } = state.auth;
    const { goalUID } = query;
    const isGTP = await fetchIsGoalGTP(goalUID, store);

    if (!state.topologies.data[goalUID]?.selfDataFetched)
      await store.dispatch(fetchTopologyInfo(goalUID));

    const { topologies = {}, practiceSession = {} } = store.getState();
    const promises = [];

    // Check if practice is available using the correct field name
    const isPracticeAvailable =
      topologies?.data?.[goalUID]?.isPracticeAvailable ||
      topologies?.data?.[goalUID]?.is_practice_session;

    if (!isPracticeAvailable) {
      if (isServer) {
        res.writeHead(302, { Location: '/' });
        return res.end();
      }

      return pushRoute('/', '/');
    }

    if (!practiceSession?.availableLanguagesByGoalUID?.[goalUID]) {
      await store.dispatch(fetchPracticeAvailableLanguages(goalUID));
    }

    const languageData = getPracticeAvailableLanguages(goalUID)(
      store.getState()
    );

    const languageCode = languageData?.user_preference ?? 0;

    if (!practiceSession?.topologies?.[goalUID])
      promises.push(
        store.dispatch(fetchPracticeTopologies({ uid: goalUID, languageCode }))
      );

    if (!topologies?.topologyDetails?.[goalUID])
      promises.push(store.dispatch(fetchTopologyInfo(goalUID)));

    // Only fetch learner details for logged-in users
    if (isLoggedIn && !practiceSession?.levelDetails?.[goalUID])
      promises.push(store.dispatch(fetchLearnerDetails(goalUID)));

    if (promises.length) await Promise.all(promises);

    // Fetch educator practices
    let practicesByEducators = {};
    try {
      practicesByEducators = await fetchPracticeByEducators(
        goalUID,
        accessToken
      );
    } catch (error) {
      // Return empty object for now - mock data can be added if needed
      practicesByEducators = {};
    }

    const goalInfo = store.getState().topologies.data[goalUID];

    if (goalInfo?.isPlatformGoal) {
      const { goalSlug } = goalInfo;
      redirectToRoute(isServer, res, `/goal/${goalSlug}/${goalUID}`);
    }

    return { goalInfo, isGTP, practicesByEducators };
  },
  PracticePage.pageInfo
);

PracticePage.propTypes = {
  goalInfo: PropTypes.objectOf(PropTypes.any).isRequired
};
PracticePage.Layout = Layout;
PracticePage.Wrapper = PageReduxProvider;
